import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { documentsApi, bullhornApi, getErrorMessage } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  Search, 
  RefreshCw, 
  RefreshCcw,
  Eye,
  CheckCircle2,
  Clock,
  AlertCircle,
  XCircle,
  FileCheck,
  ShieldCheck,
  Download,
  FileText,
  Link2,
  User,
  Building2,
  MoreHorizontal,
  Ban,
  Mail,
  Trash2,
  Shield,
  Copy,
  RotateCw,
  UserCheck,
  KeyRound,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { format } from 'date-fns';
import { Document, Page, pdfjs } from 'react-pdf';
// Stable ref so react-pdf sends the auth cookie on cross-origin (Pages→Worker)
// PDF fetches, without reloading on every render.
const PDF_HTTP_OPTIONS = { withCredentials: true };

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatusBadge = ({ status }) => {
  const config = {
    sent: { class: 'status-sent', label: 'Sent', icon: Clock },
    opened: { class: 'status-opened', label: 'Opened', icon: Eye },
    in_progress: { class: 'status-opened', label: 'In Progress', icon: RefreshCw },
    completed: { class: 'status-completed', label: 'Completed', icon: CheckCircle2 },
    pending_approval: { class: 'bg-amber-100 text-amber-700', label: 'Pending Approval', icon: FileCheck },
    approved: { class: 'bg-emerald-100 text-emerald-700', label: 'Approved', icon: ShieldCheck },
    expired: { class: 'status-expired', label: 'Expired', icon: XCircle },
    cancelled: { class: 'bg-gray-100 text-gray-600', label: 'Cancelled', icon: Ban },
  };
  
  const { class: className, label, icon: Icon } = config[status] || config.sent;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${className}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

const SyncBadge = ({ status }) => {
  const config = {
    pending: { class: 'bg-amber-50 text-amber-700', label: 'Pending' },
    syncing: { class: 'bg-blue-50 text-blue-700', label: 'Syncing' },
    success: { class: 'bg-emerald-50 text-emerald-700', label: 'Synced' },
    failed: { class: 'bg-red-50 text-red-700', label: 'Failed' },
  };
  
  const { class: className, label } = config[status] || config.pending;
  
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${className}`}>
      {label}
    </span>
  );
};

// Document Preview Modal with Link to Bullhorn feature
const DocumentPreviewModal = ({ document, open, onClose, onRefresh }) => {
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [linking, setLinking] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showAuditCertificate, setShowAuditCertificate] = useState(false);
  const [auditCertificate, setAuditCertificate] = useState(null);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  // Lazy-loaded sender-side preview PDF (overlays current `field_values`
  // on the template). Fires only for non-completed docs with values.
  const [previewPdfPath, setPreviewPdfPath] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Derived flags — safe to compute before the early-return guard since
  // they don't call hooks. Must default safely when `document` is null.
  const hasCompletedPdf = document?.completed_document_url;
  const hasPrefilledValues = (document?.field_values?.length || 0) > 0;
  const docId = document?.id;
  const fieldValuesCount = document?.field_values?.length || 0;

  useEffect(() => {
    if (!docId || hasCompletedPdf || !hasPrefilledValues) return undefined;
    let cancelled = false;
    setLoadingPreview(true);
    documentsApi
      .getPreviewPdf(docId)
      .then(({ data }) => {
        if (!cancelled && data?.preview_url) setPreviewPdfPath(data.preview_url);
      })
      .catch(() => { /* fall through to bare template */ })
      .finally(() => { if (!cancelled) setLoadingPreview(false); });
    return () => { cancelled = true; };
  }, [docId, hasCompletedPdf, hasPrefilledValues, fieldValuesCount]);

  const handleRegenerateAndSync = async () => {
    if (!document?.id) return;
    setRegenerating(true);
    try {
      const { data } = await documentsApi.regeneratePdf(document.id);
      const bh = data.bullhorn_upload || {};
      if (bh.status === 'success') {
        toast.success('PDF regenerated and synced to Bullhorn Files tab');
      } else if (bh.status === 'skipped') {
        toast.success('PDF regenerated (not linked to a Bullhorn record yet)');
      } else {
        toast.warning(`PDF regenerated, but Bullhorn upload failed: ${bh.error || 'unknown error'}`);
      }
      onRefresh?.();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to regenerate the completed PDF'));
    } finally {
      setRegenerating(false);
    }
  };

  const [resyncingCred, setResyncingCred] = useState(false);
  // Resync the existing Bullhorn credential — re-pushes expiry date and
  // re-attaches the completed PDF as a CertificationFileAttachment.
  // Idempotent: PATCHes the existing CandidateCertification rather than
  // creating a new one, so the recruiter never sees a duplicate row on
  // the candidate's Credentials tab.
  const handleResyncCredential = async () => {
    if (!document?.id) return;
    setResyncingCred(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/bullhorn/credentials/resync/${document.id}`,
      );
      const patched = res.data?.patched || {};
      const file = res.data?.file_attachment || {};
      if (patched.status === 'success' && file.status === 'success') {
        toast.success('Credential resynced — expiry + file attachment updated on Bullhorn');
      } else if (file.status === 'success') {
        toast.success('Credential file attached on Bullhorn');
      } else if (patched.status === 'success') {
        toast.success('Credential expiry updated on Bullhorn');
      } else if (file.status === 'failed' || patched.status === 'failed') {
        toast.warning(`Bullhorn rejected the update: ${file.error || patched.error || 'unknown error'}`);
      } else {
        toast.info('Nothing to resync — credential is already up to date');
      }
      onRefresh?.();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to resync credential'));
    } finally {
      setResyncingCred(false);
    }
  };
  
  if (!document) return null;

  const templateFileUrl = document.template_file_url || document.file_url;
  const isCompleted = ['completed', 'approved', 'pending_approval'].includes(document.status);
  const hasBullhornId = !!document.candidate_bullhorn_id;
  const needsPdfRegen = isCompleted && !hasCompletedPdf;
  const bullhornFileUploaded = !!document.bullhorn_file_attachment_id;
  const bullhornFileError = document.bullhorn_file_upload_error;

  // Resolve the PDF source — completed wins, otherwise the lazy preview,
  // otherwise the bare template.
  let pdfUrl = null;
  if (hasCompletedPdf) {
    pdfUrl = `${API_URL}${document.completed_document_url}`;
  } else if (previewPdfPath) {
    pdfUrl = `${API_URL}${previewPdfPath}`;
  } else if (templateFileUrl) {
    pdfUrl = `${process.env.REACT_APP_BACKEND_URL}${templateFileUrl}`;
  }

  // Build a label lookup so the Submitted Values panel can show field labels
  // (rather than raw `DOC_FIELD_xxx` ids) when `field_values[i].label` is
  // missing — historically only completion-time field_values had `.label`.
  // Also build a value→label lookup for Bullhorn picklist fields, so a
  // stored value like "2000007" renders as "Construction".
  const { fieldLabelById, picklistLabelByFieldId } = (() => {
    const labelMap = {};
    const picklistMap = {};
    for (const f of document.custom_fields || []) {
      if (f?.id) labelMap[f.id] = f.label;
      if (f?.original_id) labelMap[f.original_id] = f.label;
      const pairs = f?.bullhorn_picklist_options || [];
      if (pairs.length && f?.id) {
        picklistMap[f.id] = Object.fromEntries(pairs.map((p) => [String(p.value), p.label]));
      }
    }
    return { fieldLabelById: labelMap, picklistLabelByFieldId: picklistMap };
  })();

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const handleDownload = () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    }
  };

  const handleViewAuditCertificate = async () => {
    setLoadingAudit(true);
    try {
      const response = await documentsApi.getAuditCertificate(document.id);
      setAuditCertificate(response.data);
      setShowAuditCertificate(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load audit certificate'));
    } finally {
      setLoadingAudit(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const entityType = document.entity_type || 'candidate';
      const response = entityType === 'contact' 
        ? await bullhornApi.searchContacts(searchQuery)
        : await bullhornApi.searchCandidates(searchQuery);
      
      const results = entityType === 'contact' 
        ? response.data.contacts || []
        : response.data.candidates || [];
      setSearchResults(results);
    } catch (error) {
      toast.error('Failed to search Bullhorn');
    } finally {
      setSearching(false);
    }
  };

  const handleLinkToBullhorn = async () => {
    if (!selectedEntity) {
      toast.error('Please select a candidate/contact');
      return;
    }
    setLinking(true);
    try {
      await documentsApi.linkBullhorn(
        document.id, 
        String(selectedEntity.id), 
        document.entity_type || 'candidate'
      );
      toast.success('Document linked and synced to Bullhorn!');
      setShowLinkDialog(false);
      onRefresh();
      onClose();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to link to Bullhorn'));
    } finally {
      setLinking(false);
    }
  };

  const handleApprove = async () => {
    setApproving(true);
    try {
      await documentsApi.approve(document.id);
      toast.success('Document approved successfully!');
      onRefresh();
      onClose();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to approve document'));
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    setRejecting(true);
    try {
      await documentsApi.reject(document.id, rejectReason);
      toast.success('Document rejected');
      setShowRejectDialog(false);
      setRejectReason('');
      onRefresh();
      onClose();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to reject document'));
    } finally {
      setRejecting(false);
    }
  };

  const isPendingApproval = document.status === 'pending_approval';

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col p-0" aria-describedby="preview-modal-desc">
          <DialogHeader className="px-6 py-4 border-b flex-shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-lg font-bold">{document.template_name}</DialogTitle>
                <p className="text-sm text-muted-foreground mt-1" id="preview-modal-desc">
                  {document.candidate_name} • {document.candidate_email}
                  {document.candidate_bullhorn_id && (
                    <span className="ml-2 text-green-600 font-medium">
                      • Bullhorn ID: {document.candidate_bullhorn_id}
                    </span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {isCompleted && !hasBullhornId && (
                  <Button variant="outline" size="sm" onClick={() => setShowLinkDialog(true)}>
                    <Link2 className="w-4 h-4 mr-2" />
                    Link to Bullhorn
                  </Button>
                )}
                {isCompleted && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleViewAuditCertificate}
                    disabled={loadingAudit}
                    data-testid="view-audit-button"
                  >
                    {loadingAudit ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Shield className="w-4 h-4 mr-2" />
                    )}
                    Audit Trail
                  </Button>
                )}
                {isCompleted && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRegenerateAndSync}
                    disabled={regenerating}
                    data-testid="regenerate-pdf-button"
                    title="Rebuild the completed PDF from submitted values and re-upload to Bullhorn Files tab"
                  >
                    {regenerating ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RotateCw className="w-4 h-4 mr-2" />
                    )}
                    Regenerate &amp; Sync
                  </Button>
                )}
                {isCompleted && document?.bullhorn_credential_id && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleResyncCredential}
                    disabled={resyncingCred}
                    data-testid="resync-credential-button"
                    title="Re-push the expiry date and re-attach the PDF to the Bullhorn credential record"
                  >
                    {resyncingCred ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <ShieldCheck className="w-4 h-4 mr-2" />
                    )}
                    Resync Credential
                  </Button>
                )}
                {hasCompletedPdf && (
                  <Button variant="outline" size="sm" onClick={handleDownload}>
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                )}
              </div>
            </div>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto flex">
            {/* PDF Viewer - Left Side */}
            <div className="flex-1 bg-slate-100 p-4 overflow-auto">
              {pdfUrl ? (
                <div className="flex flex-col items-center">
                  {!hasCompletedPdf && hasPrefilledValues && previewPdfPath && (
                    <div className="w-full max-w-2xl mb-3 px-4 py-2 rounded-md bg-sky-50 border border-sky-200 text-sky-900 text-xs flex items-center gap-2" data-testid="prefill-preview-banner">
                      <Eye className="w-4 h-4" />
                      <span><strong>Preview before send</strong> — showing your pre-filled values overlaid on the template. The candidate will see the same form to verify and sign.</span>
                    </div>
                  )}
                  {!hasCompletedPdf && hasPrefilledValues && loadingPreview && !previewPdfPath && (
                    <div className="w-full max-w-2xl mb-3 px-4 py-2 rounded-md bg-slate-50 border border-slate-200 text-slate-700 text-xs flex items-center gap-2">
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      Generating preview with your pre-filled values…
                    </div>
                  )}
                  {needsPdfRegen && (
                    <div className="w-full max-w-2xl mb-3 px-4 py-3 rounded-md bg-amber-50 border border-amber-300 text-amber-900 text-xs flex items-start gap-2" data-testid="pdf-missing-banner">
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-semibold">Completed PDF unavailable</p>
                        <p className="mt-0.5">The candidate finished this form but the rendered PDF wasn't saved (likely lost during a server restart). The submitted values are intact — click <strong>Regenerate &amp; Sync</strong> above to rebuild the PDF and re-upload it to the Bullhorn Files tab.</p>
                      </div>
                    </div>
                  )}
                  {bullhornFileError && bullhornFileUploaded === false && (
                    <div className="w-full max-w-2xl mb-3 px-4 py-3 rounded-md bg-rose-50 border border-rose-300 text-rose-900 text-xs flex items-start gap-2" data-testid="pdf-bullhorn-error-banner">
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-semibold">Bullhorn Files-tab upload failed</p>
                        <p className="mt-0.5">{bullhornFileError}</p>
                      </div>
                    </div>
                  )}
                  {!hasCompletedPdf && !needsPdfRegen && (
                    <div className="w-full max-w-2xl mb-3 px-4 py-2 rounded-md bg-blue-50 border border-blue-200 text-blue-900 text-xs flex items-center gap-2" data-testid="preview-source-banner">
                      <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                      <span><strong>Original template preview</strong> — this is what was sent to the candidate. The completed copy will appear here once they submit.</span>
                    </div>
                  )}
                  {numPages && numPages > 1 && (
                    <div className="flex items-center gap-4 mb-4 bg-white px-4 py-2 rounded-lg shadow-sm">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </Button>
                      <span className="text-sm font-medium">
                        Page {currentPage} of {numPages}
                      </span>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))}
                        disabled={currentPage === numPages}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                  
                  <ErrorBoundary fallback={
                    <div className="flex flex-col items-center justify-center h-[500px] text-muted-foreground">
                      <AlertCircle className="w-12 h-12 mb-4 text-red-400" />
                      <p>PDF viewer encountered an error</p>
                      {pdfUrl && (
                        <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-primary underline mt-2">
                          Open PDF in new tab ↗
                        </a>
                      )}
                    </div>
                  }>
                  <Document
                    file={pdfUrl}
                    options={PDF_HTTP_OPTIONS}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={
                      <div className="flex items-center justify-center h-[500px]">
                        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
                      </div>
                    }
                    error={
                      <div className="flex flex-col items-center justify-center h-[500px] text-muted-foreground">
                        <AlertCircle className="w-12 h-12 mb-4 text-red-400" />
                        <p>Failed to load PDF</p>
                        {pdfUrl && (
                          <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-primary underline mt-2">
                            Open PDF in new tab ↗
                          </a>
                        )}
                      </div>
                    }
                  >
                    <Page 
                      pageNumber={currentPage} 
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      className="shadow-xl"
                      scale={0.9}
                    />
                  </Document>
                  </ErrorBoundary>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-[400px] text-muted-foreground">
                  <FileText className="w-16 h-16 mb-4 opacity-50" />
                  <p className="font-medium">PDF unavailable</p>
                  <p className="text-sm mt-2">
                    Status: <StatusBadge status={document.status} />
                  </p>
                </div>
              )}
            </div>

            {/* Field Values - Right Side */}
            <div className="w-80 border-l bg-white flex flex-col">
              <div className="p-4 border-b">
                <h3 className="font-bold">Submitted Values</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  {document.field_values?.length || 0} fields captured
                </p>
              </div>
              
              <div className="flex-1 overflow-auto p-4">
                {document.field_values && document.field_values.length > 0 ? (
                  <div className="space-y-3">
                    {document.field_values.map((fv, idx) => {
                      const value = fv.value;
                      const isSignatureImage = typeof value === 'string' && value.startsWith('data:image');
                      // Resolve label for Bullhorn picklist values (e.g.
                      // "2000007" → "Construction") so recruiters see the
                      // human-readable choice the candidate selected.
                      const picklistMap = picklistLabelByFieldId[fv.field_id];
                      const resolvedLabel = picklistMap && value != null
                        ? picklistMap[String(value)]
                        : undefined;
                      // Decide what to render — extracted out of an unreadable
                      // nested ternary so it's actually grokkable.
                      let rendered;
                      if (value === true) rendered = '✓ Yes';
                      else if (value === false) rendered = '✗ No';
                      else if (isSignatureImage) {
                        rendered = <img src={value} alt="Signature" className="h-12 mt-1" />;
                      } else if (fv.field_type === 'date' && value) {
                        rendered = format(new Date(value), 'MMMM d, yyyy');
                      } else if (resolvedLabel) {
                        rendered = (
                          <>
                            <span>{resolvedLabel}</span>
                            <span className="ml-2 text-[10px] font-mono text-muted-foreground">#{value}</span>
                          </>
                        );
                      } else {
                        rendered = String(value || '-');
                      }
                      // Stable key when the field has its own id; fall back to
                      // a position-suffixed composite (rare — only for legacy docs).
                      const key = fv.field_id || `${idx}-${fv.label || 'fv'}`;
                      // Resolve a human label: prefer the value's own .label,
                      // then the lookup from custom_fields, else show the id.
                      const headingLabel = fv.label
                        || fieldLabelById[fv.field_id]
                        || fv.field_id;
                      return (
                        <div key={key} className="p-3 rounded-lg border bg-slate-50">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            {headingLabel}
                          </p>
                          <p className="mt-1 font-medium text-sm break-words">{rendered}</p>
                          {fv.bullhorn_field && (
                            <p className="text-xs text-green-600 mt-1">
                              → Bullhorn: {fv.bullhorn_field}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="w-10 h-10 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">
                      {isCompleted ? 'No field values captured' : 'Awaiting candidate submission'}
                    </p>
                  </div>
                )}
              </div>

              {/* Status Footer */}
              <div className="p-4 border-t bg-slate-50 text-xs space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <StatusBadge status={document.status} />
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Bullhorn</span>
                  <SyncBadge status={document.bullhorn_sync_status} />
                </div>
                {/* Reference write-back confirmation. Surfaces explicit
                    "N references created" feedback so recruiters don't
                    have to guess whether the CandidateReference POSTs
                    actually landed. */}
                {Array.isArray(document.bullhorn_references_created)
                  && document.bullhorn_references_created.length > 0 && (
                  <div
                    className="rounded-md border border-emerald-200 bg-emerald-50/70 px-2.5 py-2 mt-1"
                    data-testid="references-created-block"
                  >
                    <div className="flex items-center gap-1.5 text-emerald-800 font-medium">
                      <UserCheck className="w-3.5 h-3.5" />
                      <span data-testid="references-created-count">
                        {document.bullhorn_references_created.length} reference
                        {document.bullhorn_references_created.length === 1 ? '' : 's'} created in Bullhorn
                      </span>
                    </div>
                    <ul className="mt-1.5 space-y-0.5 text-[11px] text-emerald-900/80 leading-snug">
                      {document.bullhorn_references_created.map((r) => {
                        const name = [r.first_name, r.last_name].filter(Boolean).join(' ').trim();
                        return (
                          <li key={`ref-${r.index}-${r.id}`} className="flex items-baseline gap-1.5">
                            <span className="text-emerald-700/70 tabular-nums">#{r.index}</span>
                            <span className="font-medium">{name || '(unnamed)'}</span>
                            {r.email && <span className="text-emerald-700/70">· {r.email}</span>}
                            {r.id && (
                              <span className="ml-auto font-mono text-emerald-700/70">id {r.id}</span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
                {document.sent_at && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Sent</span>
                    <span>{format(new Date(document.sent_at), 'MMM d, h:mm a')}</span>
                  </div>
                )}
                {document.completed_at && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Completed</span>
                    <span>{format(new Date(document.completed_at), 'MMM d, h:mm a')}</span>
                  </div>
                )}
                
                {/* Admin Approval Actions */}
                {isPendingApproval && (
                  <div className="pt-3 mt-3 border-t space-y-2">
                    <p className="text-xs font-medium text-amber-700 mb-2">
                      This document requires admin approval
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                        onClick={handleApprove}
                        disabled={approving || rejecting}
                        data-testid="approve-button"
                      >
                        {approving ? (
                          <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                        )}
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 text-red-600 border-red-300 hover:bg-red-50"
                        onClick={() => setShowRejectDialog(true)}
                        disabled={approving || rejecting}
                        data-testid="reject-button"
                      >
                        <XCircle className="w-3 h-3 mr-1" />
                        Reject
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Confirmation Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to reject this document? The candidate will need to resubmit.
            </p>
            <div>
              <Label className="text-sm">Rejection Reason (Optional)</Label>
              <Input
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter reason for rejection..."
                className="mt-1"
                data-testid="reject-reason-input"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowRejectDialog(false);
                  setRejectReason('');
                }} 
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleReject} 
                disabled={rejecting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-reject-button"
              >
                {rejecting ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <XCircle className="w-4 h-4 mr-2" />
                )}
                Reject Document
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Link to Bullhorn Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Link Document to Bullhorn</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-sm text-muted-foreground">
              Search for the {document.entity_type || 'candidate'} in Bullhorn to sync this completed document.
            </p>
            
            <div className="flex gap-2">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search ${document.entity_type || 'candidate'} by name...`}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button onClick={handleSearch} disabled={searching}>
                {searching ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>

            {searchResults.length > 0 && (
              <div className="max-h-60 overflow-auto border rounded-lg">
                {searchResults.map((entity) => (
                  <div
                    key={entity.id}
                    onClick={() => setSelectedEntity(entity)}
                    className={`p-3 border-b last:border-b-0 cursor-pointer transition-colors ${
                      selectedEntity?.id === entity.id ? 'bg-primary/10 border-l-4 border-l-primary' : 'hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        document.entity_type === 'contact' ? 'bg-amber-100' : 'bg-green-100'
                      }`}>
                        {document.entity_type === 'contact' ? (
                          <Building2 className="w-4 h-4 text-amber-600" />
                        ) : (
                          <User className="w-4 h-4 text-green-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{entity.name}</p>
                        <p className="text-xs text-muted-foreground">{entity.email}</p>
                      </div>
                      <span className="ml-auto text-xs text-muted-foreground">ID: {entity.id}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {selectedEntity && (
              <div className="p-3 rounded-lg bg-green-50 border border-green-200">
                <p className="text-sm font-medium text-green-800">
                  Selected: {selectedEntity.name} (ID: {selectedEntity.id})
                </p>
              </div>
            )}

            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowLinkDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleLinkToBullhorn} disabled={!selectedEntity || linking} className="flex-1">
                {linking ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
                Link & Sync
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Audit Certificate Dialog */}
      <Dialog open={showAuditCertificate} onOpenChange={setShowAuditCertificate}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-emerald-600" />
              E-Signature Audit Certificate
            </DialogTitle>
          </DialogHeader>
          
          {auditCertificate && (
            <div className="space-y-6">
              {/* Certificate Header */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-mono text-emerald-700 uppercase tracking-wider">Certificate ID</span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => copyToClipboard(auditCertificate.certificate_id)}
                    className="h-6 px-2"
                  >
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
                <p className="font-mono font-bold text-emerald-900">{auditCertificate.certificate_id}</p>
              </div>

              {/* Document Info */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">Document Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-1">Document Name</p>
                    <p className="font-medium">{auditCertificate.document_name}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-1">Status</p>
                    <p className="font-medium capitalize">{auditCertificate.document_status?.replace('_', ' ')}</p>
                  </div>
                </div>
              </div>

              {/* Signer Info */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">Signer Information</h3>
                <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium">{auditCertificate.signer?.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span>{auditCertificate.signer?.email}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Entity Type: <span className="capitalize">{auditCertificate.signer?.entity_type}</span>
                  </div>
                </div>
              </div>

              {/* Submission Details */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">Submission Details</h3>
                <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Submitted At</p>
                    <p className="font-mono text-sm">
                      {auditCertificate.submission?.timestamp 
                        ? format(new Date(auditCertificate.submission.timestamp), 'PPpp')
                        : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">IP Address</p>
                    <p className="font-mono text-sm">{auditCertificate.submission?.ip_address || 'Not recorded'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">User Agent</p>
                    <p className="font-mono text-xs break-all">{auditCertificate.submission?.user_agent || 'Not recorded'}</p>
                  </div>
                </div>
              </div>

              {/* Signatures */}
              {auditCertificate.signatures?.length > 0 && (
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">Signature Events</h3>
                  <div className="space-y-2">
                    {auditCertificate.signatures.map((sig, idx) => {
                      const sigKey = `${sig.field_label || 'sig'}-${sig.signed_at || idx}`;
                      return (
                        <div key={sigKey} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-2">
                            <FileCheck className="w-4 h-4 text-blue-600" />
                            <span className="font-medium text-blue-900">{sig.field_label || 'Signature'}</span>
                          </div>
                          <div className="text-xs text-blue-700 space-y-1">
                            <p>Signed: {format(new Date(sig.signed_at), 'PPpp')}</p>
                            <p>IP: {sig.ip_address}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Timeline */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">Document Timeline</h3>
                <div className="border rounded-lg divide-y">
                  {auditCertificate.timeline?.sent_at && (
                    <div className="flex items-center justify-between p-3">
                      <span className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-blue-500" />
                        Sent
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {format(new Date(auditCertificate.timeline.sent_at), 'PPp')}
                      </span>
                    </div>
                  )}
                  {auditCertificate.timeline?.opened_at && (
                    <div className="flex items-center justify-between p-3">
                      <span className="flex items-center gap-2">
                        <Eye className="w-4 h-4 text-amber-500" />
                        Opened
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {format(new Date(auditCertificate.timeline.opened_at), 'PPp')}
                      </span>
                    </div>
                  )}
                  {auditCertificate.timeline?.completed_at && (
                    <div className="flex items-center justify-between p-3">
                      <span className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        Completed
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {format(new Date(auditCertificate.timeline.completed_at), 'PPp')}
                      </span>
                    </div>
                  )}
                  {auditCertificate.timeline?.approved_at && (
                    <div className="flex items-center justify-between p-3">
                      <span className="flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-purple-500" />
                        Approved
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {format(new Date(auditCertificate.timeline.approved_at), 'PPp')}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Verification Hash */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">Verification</h3>
                <div className="bg-slate-800 rounded-lg p-4 text-white">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-slate-400">{auditCertificate.verification?.algorithm} Hash</span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => copyToClipboard(auditCertificate.verification?.hash)}
                      className="h-6 px-2 text-slate-400 hover:text-white"
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                  <p className="font-mono text-xs break-all text-emerald-400">
                    {auditCertificate.verification?.hash}
                  </p>
                  <p className="text-xs text-slate-400 mt-2">
                    Fields submitted: {auditCertificate.verification?.field_count}
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="text-center text-xs text-muted-foreground border-t pt-4">
                <p>Certificate generated at {format(new Date(auditCertificate.generated_at), 'PPpp')}</p>
                <p className="mt-1">This certificate provides a tamper-evident record of the e-signature process.</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default function TrackingPage({ embedded = false, highlightLatest = false }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.is_master_admin;
  const [searchParams, setSearchParams] = useSearchParams();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  // Initialize from `?status=` so dashboard stat-card clicks deep-link
  // straight into the filtered tracking view (e.g. /tracking?status=sent).
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') || 'all');
  const [syncing, setSyncing] = useState(null);
  const [previewDocument, setPreviewDocument] = useState(null);
  
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkOperating, setBulkOperating] = useState(false);
  const [sortField, setSortField] = useState('sent_at');
  const [sortDir, setSortDir] = useState('desc');

  // Keep the URL in sync with the dropdown so refreshes / bookmarks
  // preserve the filter, and so the user can clear the deep-link by
  // switching back to "All". Skipped when embedded inside DocumentsPage
  // because that parent already manages `?view=...` on the same URL and
  // we don't want two components fighting over query params.
  useEffect(() => {
    if (embedded) return;
    const current = searchParams.get('status') || 'all';
    if (statusFilter === current) return;
    const next = new URLSearchParams(searchParams);
    if (statusFilter === 'all') {
      next.delete('status');
    } else {
      next.set('status', statusFilter);
    }
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    try {
      // Sync names from Bullhorn in the background (non-blocking)
      axios.post(`${API_URL}/api/documents/sync-bullhorn-names`).catch(() => {});
      const params = statusFilter !== 'all' ? statusFilter : undefined;
      const response = await documentsApi.list(params);
      setDocuments(response.data);
      setSelectedIds(new Set());
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const handleSync = async (docId) => {
    setSyncing(docId);
    try {
      await bullhornApi.syncDocument(docId);
      toast.success('Document synced to Bullhorn');
      loadDocuments();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Sync failed'));
    } finally {
      setSyncing(null);
    }
  };
  
  // Toggle selection for a single document
  const toggleSelection = (docId) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(docId)) {
        newSet.delete(docId);
      } else {
        newSet.add(docId);
      }
      return newSet;
    });
  };
  
  // Toggle select all (visible filtered documents)
  const toggleSelectAll = () => {
    if (selectedIds.size === filteredDocuments.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredDocuments.map(d => d.id)));
    }
  };
  
  // Handle bulk operation
  const handleBulkOperation = async (operation) => {
    if (selectedIds.size === 0) {
      toast.error('No documents selected');
      return;
    }
    
    // Hard confirm for destructive permanent delete
    if (operation === 'delete') {
      const confirmed = window.confirm(
        `Permanently delete ${selectedIds.size} document${selectedIds.size > 1 ? 's' : ''}?\n\n` +
        `This removes the record from HyreDocs. Signed PDFs already synced to Bullhorn are NOT affected.\n\nThis cannot be undone.`
      );
      if (!confirmed) return;
    }
    
    setBulkOperating(true);
    try {
      const result = await documentsApi.bulkOperation(Array.from(selectedIds), operation);
      
      const actionVerb = {
        expire: 'expired',
        resend: 'sent reminders for',
        cancel: 'cancelled',
        delete: 'deleted',
      }[operation] || 'updated';
      
      if (result.data.failed_count === 0) {
        toast.success(`Successfully ${actionVerb} ${result.data.success_count} document${result.data.success_count !== 1 ? 's' : ''}`);
      } else {
        toast.warning(`${result.data.success_count} succeeded, ${result.data.failed_count} failed`);
      }
      
      setSelectedIds(new Set());
      loadDocuments();
    } catch (error) {
      toast.error(getErrorMessage(error, `Bulk ${operation} failed`));
    } finally {
      setBulkOperating(false);
    }
  };

  const filteredDocuments = documents.filter(doc => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      doc.candidate_name?.toLowerCase().includes(query) ||
      doc.candidate_email?.toLowerCase().includes(query) ||
      doc.template_name?.toLowerCase().includes(query)
    );
  }).sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    let aVal, bVal;
    switch (sortField) {
      case 'candidate_name':
        aVal = (a.candidate_name || '').toLowerCase();
        bVal = (b.candidate_name || '').toLowerCase();
        return aVal < bVal ? -dir : aVal > bVal ? dir : 0;
      case 'template_name':
        aVal = (a.template_name || '').toLowerCase();
        bVal = (b.template_name || '').toLowerCase();
        return aVal < bVal ? -dir : aVal > bVal ? dir : 0;
      case 'status':
        const statusOrder = { sent: 0, opened: 1, in_progress: 2, pending_approval: 3, completed: 4, approved: 5, expired: 6, cancelled: 7 };
        aVal = statusOrder[a.status] ?? 99;
        bVal = statusOrder[b.status] ?? 99;
        return (aVal - bVal) * dir;
      case 'sent_at':
        aVal = a.sent_at || '';
        bVal = b.sent_at || '';
        return aVal < bVal ? -dir : aVal > bVal ? dir : 0;
      case 'completed_at':
        aVal = a.completed_at || '';
        bVal = b.completed_at || '';
        return aVal < bVal ? -dir : aVal > bVal ? dir : 0;
      default:
        return 0;
    }
  });

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
    return sortDir === 'asc' ? <ArrowUp className="w-3 h-3 ml-1" /> : <ArrowDown className="w-3 h-3 ml-1" />;
  };
  
  // Check if selected docs can be operated on
  const canExpireSelected = () => {
    return Array.from(selectedIds).some(id => {
      const doc = documents.find(d => d.id === id);
      return doc && ['sent', 'opened', 'in_progress'].includes(doc.status);
    });
  };
  
  const canResendSelected = () => {
    return Array.from(selectedIds).some(id => {
      const doc = documents.find(d => d.id === id);
      return doc && ['sent', 'opened', 'in_progress'].includes(doc.status);
    });
  };
  
  const canCancelSelected = () => {
    return Array.from(selectedIds).some(id => {
      const doc = documents.find(d => d.id === id);
      return doc && ['sent', 'opened', 'in_progress', 'expired'].includes(doc.status);
    });
  };

  const content = (
    <div className="space-y-8" data-testid="tracking-page">
      {/* Header - only show when not embedded */}
      {!embedded && (
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Document Tracking</h1>
          <p className="text-muted-foreground font-body mt-1">
            Track sent documents and sync status
          </p>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by candidate or template..."
            className="pl-10 input-focus"
            data-testid="search-documents-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="opened">Opened</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="pending_approval">Pending Approval</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={loadDocuments} variant="outline" data-testid="refresh-button">
          <RefreshCcw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>
        
        {/* Bulk Actions Bar - shows when items selected */}
        {selectedIds.size > 0 && (
          <div className="flex items-center justify-between p-4 bg-primary/5 rounded-lg border border-primary/20">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium">
                {selectedIds.size} document{selectedIds.size > 1 ? 's' : ''} selected
              </span>
              <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>
                Clear
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleBulkOperation('resend')}
                disabled={bulkOperating || !canResendSelected()}
                data-testid="bulk-resend-button"
              >
                {bulkOperating ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
                Send Reminders
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                className="text-amber-600 border-amber-300 hover:bg-amber-50"
                onClick={() => handleBulkOperation('expire')}
                disabled={bulkOperating || !canExpireSelected()}
                data-testid="bulk-expire-button"
              >
                {bulkOperating ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Ban className="w-4 h-4 mr-1" />}
                Expire Selected
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                className="text-red-600 border-red-300 hover:bg-red-50"
                onClick={() => handleBulkOperation('cancel')}
                disabled={bulkOperating || !canCancelSelected()}
                title={!canCancelSelected() ? 'Only in-flight documents (sent / opened / expired) can be cancelled. Completed documents must be deleted instead.' : ''}
                data-testid="bulk-cancel-button"
              >
                {bulkOperating ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Ban className="w-4 h-4 mr-1" />}
                Cancel Selected
              </Button>
              {isAdmin && (
                <Button 
                  variant="outline" 
                  size="sm"
                  className="text-red-700 border-red-400 hover:bg-red-50 hover:text-red-800"
                  onClick={() => handleBulkOperation('delete')}
                  disabled={bulkOperating}
                  title="Permanently delete selected documents (admin only). Signed PDFs synced to Bullhorn are unaffected."
                  data-testid="bulk-delete-button"
                >
                  {bulkOperating ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
                  Delete Selected
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Documents Table */}
        <div className="neumorphic rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filteredDocuments.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-10">
                    <Checkbox
                      checked={selectedIds.size === filteredDocuments.length && filteredDocuments.length > 0}
                      onCheckedChange={toggleSelectAll}
                      aria-label="Select all"
                      data-testid="select-all-checkbox"
                    />
                  </TableHead>
                  <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort('candidate_name')}>
                    <span className="inline-flex items-center">Candidate<SortIcon field="candidate_name" /></span>
                  </TableHead>
                  <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort('template_name')}>
                    <span className="inline-flex items-center">Template<SortIcon field="template_name" /></span>
                  </TableHead>
                  <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort('status')}>
                    <span className="inline-flex items-center">Status<SortIcon field="status" /></span>
                  </TableHead>
                  <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort('sent_at')}>
                    <span className="inline-flex items-center">Sent<SortIcon field="sent_at" /></span>
                  </TableHead>
                  <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort('completed_at')}>
                    <span className="inline-flex items-center">Completed<SortIcon field="completed_at" /></span>
                  </TableHead>
                  <TableHead>Bullhorn</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDocuments.map((doc, index) => (
                  <TableRow 
                    key={doc.id}
                    className={`animate-fade-in opacity-0 cursor-pointer hover:bg-muted/30 ${selectedIds.has(doc.id) ? 'bg-primary/5' : ''}`}
                    style={{ animationDelay: `${index * 0.05}s` }}
                    data-testid={`document-row-${doc.id}`}
                    onClick={() => setPreviewDocument(doc)}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedIds.has(doc.id)}
                        onCheckedChange={() => toggleSelection(doc.id)}
                        aria-label={`Select ${doc.candidate_name}`}
                        data-testid={`select-checkbox-${doc.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          doc.entity_type === 'contact' || (doc.entity_type === 'placement' && doc.placement_recipient_type === 'contact')
                            ? 'bg-amber-100'
                            : doc.entity_type === 'placement'
                              ? 'bg-[#1a4a5e]/10'
                              : 'bg-green-100'
                        }`}>
                          {doc.entity_type === 'contact' || (doc.entity_type === 'placement' && doc.placement_recipient_type === 'contact') ? (
                            <Building2 className="w-4 h-4 text-amber-600" />
                          ) : doc.entity_type === 'placement' ? (
                            <User className="w-4 h-4 text-[#1a4a5e]" />
                          ) : (
                            <User className="w-4 h-4 text-green-600" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <p className="font-medium text-sm truncate">{doc.candidate_name}</p>
                            {/* Placement docs: surface which side of the placement
                                was reached so recruiters can see at a glance
                                whether the candidate, contact, or both were
                                emailed. Renders as a small color-coded chip. */}
                            {doc.entity_type === 'placement' && doc.placement_recipient_type && (
                              <span
                                className={`text-[10px] font-medium px-1.5 py-0.5 rounded uppercase tracking-wide ${
                                  doc.placement_recipient_type === 'contact'
                                    ? 'bg-amber-100 text-amber-800'
                                    : 'bg-green-100 text-green-800'
                                }`}
                                data-testid={`placement-recipient-badge-${doc.id}`}
                                title={`Document sent to the placement's ${doc.placement_recipient_type}`}
                              >
                                → {doc.placement_recipient_type === 'contact' ? 'Contact' : 'Candidate'}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground truncate">{doc.candidate_email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{doc.template_name}</TableCell>
                    <TableCell>
                      <StatusBadge status={doc.status} />
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {doc.sent_at && format(new Date(doc.sent_at), 'MMM d, h:mm a')}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {doc.completed_at ? format(new Date(doc.completed_at), 'MMM d, h:mm a') : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <SyncBadge status={doc.bullhorn_sync_status} />
                        {doc.candidate_bullhorn_id && (
                          <span className="text-xs text-muted-foreground">#{doc.candidate_bullhorn_id}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewDocument(doc);
                          }}
                          data-testid={`preview-button-${doc.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {['completed', 'approved', 'pending_approval'].includes(doc.status) && 
                         doc.bullhorn_sync_status !== 'success' && 
                         doc.candidate_bullhorn_id && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSync(doc.id);
                            }}
                            disabled={syncing === doc.id}
                            data-testid={`sync-button-${doc.id}`}
                          >
                            {syncing === doc.id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <RefreshCcw className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                        {/* More actions dropdown */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button size="sm" variant="ghost">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setPreviewDocument(doc)}>
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            {['sent', 'opened', 'in_progress'].includes(doc.status) && (
                              <>
                                <DropdownMenuItem onClick={async () => {
                                  try {
                                    await documentsApi.bulkOperation([doc.id], 'resend');
                                    toast.success('Reminder sent');
                                  } catch (error) {
                                    toast.error(getErrorMessage(error, 'Failed to send reminder'));
                                  }
                                }}>
                                  <Mail className="w-4 h-4 mr-2" />
                                  Send Reminder
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  className="text-red-600"
                                  onClick={async () => {
                                    try {
                                      await documentsApi.cancel(doc.id);
                                      toast.success('Document cancelled');
                                      loadDocuments();
                                    } catch (error) {
                                      toast.error(getErrorMessage(error, 'Failed to cancel document'));
                                    }
                                  }}
                                >
                                  <Ban className="w-4 h-4 mr-2" />
                                  Cancel Document
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-16">
              <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground font-body mb-2">No documents found</p>
              <p className="text-sm text-muted-foreground">
                {searchQuery || statusFilter !== 'all' 
                  ? 'Try adjusting your search or filters'
                  : 'Start by sending a document to a candidate'
                }
              </p>
            </div>
          )}
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          {[
            { label: 'Total', value: documents.length, color: 'bg-primary' },
            { label: 'Sent', value: documents.filter(d => d.status === 'sent').length, color: 'bg-info' },
            { label: 'In Progress', value: documents.filter(d => ['opened', 'in_progress'].includes(d.status)).length, color: 'bg-warning' },
            { label: 'Completed', value: documents.filter(d => ['completed', 'approved', 'pending_approval'].includes(d.status)).length, color: 'bg-success' },
            { label: 'Expired', value: documents.filter(d => d.status === 'expired').length, color: 'bg-error' },
            { label: 'Cancelled', value: documents.filter(d => d.status === 'cancelled').length, color: 'bg-gray-400' },
          ].map(({ label, value, color }) => (
            <div key={label} className="neumorphic rounded-xl p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
              <p className="font-heading text-2xl font-bold mt-1">{value}</p>
            </div>
          ))}
        </div>

      {/* Document Preview Modal — wrapped in ErrorBoundary to prevent
           PDF rendering crashes from taking down the entire page */}
      <ErrorBoundary>
        <DocumentPreviewModal 
          document={previewDocument}
          open={!!previewDocument}
          onClose={() => setPreviewDocument(null)}
          onRefresh={loadDocuments}
        />
      </ErrorBoundary>
    </div>
  );

  // If embedded, return content directly without DashboardLayout
  if (embedded) {
    return content;
  }

  return (
    <DashboardLayout>
      {content}
    </DashboardLayout>
  );
}
